import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { ConvexProvider } from "convex/react";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { trackAdClickFromUrl } from "../lib/adTracking";
import { trackPageView } from "../lib/metaPixel";
import { trackGtagPageView } from "../lib/gtag";
import { Samtykke } from "../components/Samtykke";
import { getConvexClient } from "../lib/convex";
import { AscendlyTema } from "../lib/innhold";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Drammen Car Wash" },
      // Eierskapsbevis for Google Search Console. Ascendly drifter siden, og
      // verifiserer derfor eierskapet selv i stedet for aa be kunden gjoere
      // det. Uten Search Console kan vi ikke maale om artiklene gir klikk.
      {
        name: "google-site-verification",
        content: "oJ4H6ws2ckl1AVLsbwK11AyKbt9eGHMMUtWxw7uHJNY",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Drammen Car Wash" },
      { property: "og:title", content: "Drammen Car Wash" },
      { name: "twitter:title", content: "Drammen Car Wash" },
      { name: "description", content: "Drammen Car Wash tilbyr premium bilvask, dekkskift og oljeskift i Drammen, med et enkelt bookingsystem." },
      { property: "og:description", content: "Drammen Car Wash tilbyr premium bilvask, dekkskift og oljeskift i Drammen, med et enkelt bookingsystem." },
      { name: "twitter:description", content: "Drammen Car Wash tilbyr premium bilvask, dekkskift og oljeskift i Drammen, med et enkelt bookingsystem." },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "icon", href: "/favicon-32x32.png", type: "image/png", sizes: "32x32" },
      { rel: "icon", href: "/favicon-192x192.png", type: "image/png", sizes: "192x192" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      // Self-hosted fonts: same-origin, so no preconnect/DNS round-trip to
      // fonts.googleapis.com blocking first render.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/Inter-var-latin.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/PlayfairDisplay-var-latin.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: "/fonts/fonts.css" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="nb">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        {/* Ikke noe noscript-piksel her. Det fyrte for alle uten JavaScript,
            foer noen hadde sagt ja, og et samtykke som bare finnes i
            nettleseren kan ikke stoppe et bilde som allerede staar i HTML-en. */}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  // Annonseklikket registreres hos oss selv, i Convex, og er det kunden
  // trenger for aa se hvilken annonse som ga bestillingen. Maalingen fra
  // Google og Meta lastes derimot ikke her, men av samtykkebanneret.
  useEffect(() => {
    void trackAdClickFromUrl();
  }, []);

  // Ingen av taggene teller rutebytter selv, saa begge maa faa sidevisningen
  // eksplisitt. Begge gjoer ingenting foer samtykke er gitt.
  useEffect(() => {
    return router.subscribe("onResolved", ({ fromLocation, toLocation }) => {
      if (fromLocation?.pathname !== toLocation.pathname) {
        trackPageView();
        trackGtagPageView(toLocation.pathname);
      }
    });
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <ConvexProvider client={getConvexClient()}>
        <AscendlyTema />
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        <Samtykke />
      </ConvexProvider>
    </QueryClientProvider>
  );
}
